import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { formatDeShort } from "@/lib/date";

const eur = (v) => `${(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const MON = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("de-DE", { month: "short", year: "2-digit" });
const STATUS = { paid: ["bezahlt", "text-emerald-700"], failed: ["fehlgeschlagen", "text-red-600"], open: ["offen", "text-amber-600"], refunded: ["erstattet", "text-slate-500"], void: ["storniert", "text-slate-400"] };

function Kpi({ label, value, testId }) {
  return <div className="border border-slate-200 bg-white p-4" data-testid={testId}><div className="text-[10px] uppercase tracking-overline text-slate-500">{label}</div><div className="text-2xl font-light mt-1">{value}</div></div>;
}

export const PlatformRevenue = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/platform/revenue").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  const csv = async () => {
    const res = await api.get("/platform/revenue/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "plattform-umsaetze.csv";
    a.click();
  };
  if (d === null) return <div className="mt-10 text-xs text-slate-400">Umsätze laden…</div>;
  if (!d) return null;
  return (
    <div className="mt-10 space-y-5" data-testid="platform-revenue">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Plattform-Umsätze</div>
        <button onClick={csv} className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="revenue-csv"><Download size={12} /> CSV exportieren</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Umsatz dieser Monat" value={eur(d.kpis.month)} testId="revenue-kpi-month" />
        <Kpi label="Umsatz Vormonat" value={eur(d.kpis.prev_month)} testId="revenue-kpi-prev" />
        <Kpi label="Aktive Abos" value={d.kpis.active_subs} testId="revenue-kpi-subs" />
        <Kpi label="Monatl. wiederkehrend (MRR)" value={eur(d.kpis.mrr)} testId="revenue-kpi-mrr" />
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs min-w-[1100px]" data-testid="revenue-by-tenant">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-overline text-slate-500 text-left"><th className="px-4 py-3">Träger</th>{d.months.map((m) => <th key={m} className="px-2 text-right">{MON(m)}</th>)}<th className="px-4 text-right">Summe</th></tr></thead>
          <tbody>
            {d.by_tenant.length === 0 && <tr><td colSpan={14} className="px-4 py-4 text-slate-400">Noch keine Zahlungen.</td></tr>}
            {d.by_tenant.map((r) => (
              <tr key={r.tenant} className="border-t border-slate-100"><td className="px-4 py-2">{r.name}</td>{d.months.map((m) => <td key={m} className="px-2 text-right font-mono">{r.months[m] ? eur(r.months[m]) : "–"}</td>)}<td className="px-4 text-right font-mono font-semibold">{eur(r.total)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="revenue-payments">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-overline text-slate-500 text-left sticky top-0"><th className="px-4 py-3">Datum</th><th className="px-3">Träger</th><th className="px-3">Tarif</th><th className="px-3">Art</th><th className="px-3 text-right">Betrag</th><th className="px-3">Status</th><th className="px-3" /></tr></thead>
          <tbody>
            {d.payments.length === 0 && <tr><td colSpan={7} className="px-4 py-4 text-xs text-slate-400">Noch keine Zahlungen.</td></tr>}
            {d.payments.map((p) => {
              const [lbl, cls] = STATUS[p.status] || [p.status, "text-slate-500"];
              return (
                <tr key={p.invoice_id} className="border-t border-slate-100" data-testid={`revenue-row-${p.invoice_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{p.date ? formatDeShort(p.date.slice(0, 10)) : "—"}</td><td className="px-3">{p.name}</td><td className="px-3">{p.plan_name}</td>
                  <td className="px-3 text-xs">{p.kind === "abo" ? "Abo" : "Einmalig"}</td><td className="px-3 text-right font-mono">{eur(p.amount)}</td>
                  <td className={`px-3 text-xs ${cls}`}>{lbl}</td><td className="px-3 text-xs">{p.invoice_url && <a href={p.invoice_url} target="_blank" rel="noreferrer" className="underline">Rechnung</a>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
